import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import toastUtils from "../../utils/toastUtils";
import * as Yup from "yup";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Tabs,
  Tab,
  Box,
  Paper,
  Typography,
  Divider,
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useCompanySettingsContext } from "../../context/CompanySettings/CompanySettingsContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: theme.palette.primary.main,
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  tabPanel: {
    padding: theme.spacing(2, 0),
  },
  colorPicker: {
    width: "100%",
    height: 40,
    border: "1px solid #ccc",
    borderRadius: 4,
    cursor: "pointer",
  },
  previewContainer: {
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    padding: theme.spacing(2),
    backgroundColor: "#f5f5f5",
    minHeight: 200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  widgetPreview: {
    position: "relative",
    width: 300,
    height: 400,
    backgroundColor: "white",
    borderRadius: 12,
    boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  widgetHeader: {
    padding: theme.spacing(2),
    color: "white",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  widgetHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  widgetLogo: {
    height: 24,
    width: "auto",
    marginRight: theme.spacing(1),
  },
  widgetBody: {
    flex: 1,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "center",
    color: "#666",
    overflowY: "auto",
  },
  widgetInputArea: {
    padding: theme.spacing(1.5),
    backgroundColor: "#ffffff",
    borderTop: "1px solid #e0e0e0",
  },
  widgetInput: {
    width: "100%",
    border: "1px solid #ddd",
    borderRadius: 20,
    padding: "8px 12px",
    fontSize: "0.875rem",
    color: "#999",
    backgroundColor: "#f9f9f9",
  },
  widgetFooter: {
    padding: theme.spacing(0.5),
    backgroundColor: "#f0f0f0",
    borderTop: "1px solid #e0e0e0",
    fontSize: "0.65rem",
    color: "#666",
    textAlign: "center",
  },
  codeContainer: {
    backgroundColor: "#f4f4f4",
    padding: theme.spacing(2),
    borderRadius: 4,
    fontFamily: "monospace",
    fontSize: "0.875rem",
    wordBreak: "break-all",
  },
}));

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`webchat-tabpanel-${index}`}
      aria-labelledby={`webchat-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const WebChatConfigModal = ({ open, onClose, webChat, onSave }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [queues, setQueues] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [loadingAssistants, setLoadingAssistants] = useState(false);
  const { getSetting } = useCompanySettingsContext();

  // Buscar configurações do whitelabel
  const primaryColor = getSetting("primaryColor", "#666666");
  const primaryColorDark = getSetting("primaryColorDark", "#444444");
  const appLogoLight = getSetting("appLogoLight", null);
  const appLogoDark = getSetting("appLogoDark", null);
  const appName = getSetting("appName", "Sistema");

  const [config, setConfig] = useState({
    name: "",
    allowedOrigins: "",
    enabled: true,
    widgetColor: "#2563eb",
    widgetPosition: "right",
    welcomeMessage: i18n.t("settings.webchat.placeholders.welcomeMessage"),
    messagePlaceholder: i18n.t("settings.webchat.placeholders.messagePlaceholder"),
    widgetTitle: i18n.t("settings.webchat.placeholders.widgetTitle"),
    widgetDescription: i18n.t("settings.webchat.placeholders.widgetDescription"),
    customCSS: "",
    autoAssign: true,
    queueId: "",
    respectBusinessHours: true,
    maxConcurrentChats: 5,
    offlineMessage: i18n.t("settings.webchat.placeholders.offlineMessage"),
    showAgentInfo: true,
    allowFileUpload: true,
    allowedFileTypes: "jpg,jpeg,png,pdf,doc,docx",
    maxFileSize: 10,
    requireName: true,
    requireEmail: false,
    requirePhone: false,
    enableAssistant: false,
    assistantId: null,
    companyName: appName,
    companyUrl: "",
    companyLogoUrl: null,
  });

  const WebChatSchema = Yup.object().shape({
    name: Yup.string().required(i18n.t("settings.webchat.validation.nameRequired")).max(50, i18n.t("settings.webchat.validation.maxLength", { max: 50 })),
    allowedOrigins: Yup.string().test("valid-urls", i18n.t("settings.webchat.validation.invalidOrigins"), function(value) {
      if (!value || value.trim() === "") return true;
      const origins = value.split("\n").map(o => o.trim()).filter(o => o.length > 0);
      for (const origin of origins) {
        if (!origin.match(/^https?:\/\/.+/i) && !origin.startsWith("*.")) {
          return false;
        }
      }
      return true;
    }),
    widgetColor: Yup.string()
      .matches(/^#[0-9A-F]{6}$/i, i18n.t("settings.webchat.validation.colorFormat"))
      .required(i18n.t("settings.webchat.validation.required")),
    widgetTitle: Yup.string().required(i18n.t("settings.webchat.validation.required")).max(50, i18n.t("settings.webchat.validation.maxLength", { max: 50 })),
    welcomeMessage: Yup.string().required(i18n.t("settings.webchat.validation.required")).max(500, i18n.t("settings.webchat.validation.maxLength", { max: 500 })),
    maxConcurrentChats: Yup.number().positive(i18n.t("settings.webchat.validation.positive")).max(50, i18n.t("settings.webchat.validation.maxValue", { max: 50 })),
    maxFileSize: Yup.number().positive(i18n.t("settings.webchat.validation.positive")).max(100, i18n.t("settings.webchat.validation.maxValue", { max: 100 })),
    companyName: Yup.string().max(50, i18n.t("settings.webchat.validation.maxLength", { max: 50 })),
    companyUrl: Yup.string().max(100, i18n.t("settings.webchat.validation.maxLength", { max: 100 })),
  });

  useEffect(() => {
    if (open) {
      fetchQueues();
      fetchAssistants();

      if (webChat) {
        // Editando WebChat existente
        setConfig({
          ...webChat,
          allowedOrigins: webChat.allowedOrigins ? webChat.allowedOrigins.split(",").join("\n") : "",
          customCSS: webChat.customCSS || "",
          queueId: webChat.queueId || "",
          assistantId: webChat.assistantId || null,
          companyName: webChat.companyName || appName,
        });
      } else {
        // Criando novo WebChat - resetar para valores padrão
        setConfig({
          name: "",
          allowedOrigins: "",
          enabled: true,
          widgetColor: primaryColor,
          widgetPosition: "right",
          welcomeMessage: i18n.t("settings.webchat.placeholders.welcomeMessage"),
          messagePlaceholder: i18n.t("settings.webchat.placeholders.messagePlaceholder"),
          widgetTitle: i18n.t("settings.webchat.placeholders.widgetTitle"),
          widgetDescription: i18n.t("settings.webchat.placeholders.widgetDescription"),
          customCSS: "",
          autoAssign: true,
          queueId: "",
          respectBusinessHours: true,
          maxConcurrentChats: 5,
          offlineMessage: i18n.t("settings.webchat.placeholders.offlineMessage"),
          showAgentInfo: true,
          allowFileUpload: true,
          allowedFileTypes: "jpg,jpeg,png,pdf,doc,docx",
          maxFileSize: 10,
          requireName: true,
          requireEmail: false,
          requirePhone: false,
          enableAssistant: false,
          assistantId: null,
          companyName: appName,
          companyUrl: "",
          companyLogoUrl: appLogoLight,
        });
      }
      setTabValue(0);
    }
  }, [open, webChat]);

  const fetchQueues = async () => {
    try {
      const { data } = await api.get("/queue");
      setQueues(data);
    } catch (err) {
      toastError(err);
    }
  };

  const fetchAssistants = async () => {
    try {
      setLoadingAssistants(true);
      const { data } = await api.get("/assistants", {
        params: {
          pageNumber: 1,
          pageSize: 100,
        }
      });
      setAssistants(data.assistants || []);
    } catch (err) {
      console.error("Erro ao carregar assistentes:", err);
      toastError(err);
    } finally {
      setLoadingAssistants(false);
    }
  };

  const handleSaveConfig = async (values) => {
    try {
      setLoading(true);

      // Processar origens permitidas: transformar linhas em CSV
      const allowedOrigins = values.allowedOrigins
        ? values.allowedOrigins
            .split("\n")
            .map(o => o.trim())
            .filter(o => o.length > 0)
            .join(",")
        : "";

      // Tratar campos especiais
      const processedValues = {
        ...values,
        allowedOrigins: allowedOrigins || null,
        queueId: values.queueId === "" || values.queueId === null ? null : parseInt(values.queueId),
        maxConcurrentChats: parseInt(values.maxConcurrentChats),
        maxFileSize: parseInt(values.maxFileSize),
        assistantId: values.assistantId === "" || values.assistantId === null ? null : values.assistantId,
        enableAssistant: Boolean(values.enableAssistant),
      };

      if (webChat?.id) {
        // Editar existente
        await api.put(`/webchat/configs/${webChat.id}`, processedValues);
        toastUtils.success(i18n.t("settings.webchat.messages.updateSuccess"), i18n.t("settings.webchat.messages.updated"));
      } else {
        // Criar novo
        await api.post("/webchat/configs", processedValues);
        toastUtils.success(i18n.t("settings.webchat.messages.createSuccess"), i18n.t("settings.webchat.messages.created"));
      }

      if (onSave) onSave();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const generateEmbedCode = (token) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";
    return `<!-- WebChat Widget -->
<script>
  (function() {
    var script = document.createElement('script');
    script.src = '${backendUrl}/widget/webchat.js';
    script.dataset.token = '${token || "SEU_TOKEN_AQUI"}';
    script.async = true;
    document.body.appendChild(script);
  })();
</script>`;
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {webChat ? i18n.t("settings.webchat.form.editTitle") : i18n.t("settings.webchat.form.createTitle")}
      </DialogTitle>
      <DialogContent dividers>
        <Formik
          initialValues={config}
          enableReinitialize
          validationSchema={WebChatSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveConfig(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, setFieldValue, isSubmitting }) => (
            <Form>
              <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
                <Tab label={i18n.t("settings.webchat.tabs.appearance")} />
                <Tab label={i18n.t("settings.webchat.tabs.behavior")} />
                <Tab label={i18n.t("settings.webchat.tabs.files")} />
                <Tab label={i18n.t("settings.webchat.tabs.customization")} />
                <Tab label={i18n.t("settings.webchat.tabs.code")} />
              </Tabs>

              <TabPanel value={tabValue} index={0} className={classes.tabPanel}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper className={classes.previewContainer}>
                      <div className={classes.widgetPreview}>
                        <div
                          className={classes.widgetHeader}
                          style={{ backgroundColor: primaryColor }}
                        >
                          <div className={classes.widgetHeaderLeft}>
                            {appLogoLight && (
                              <img
                                src={appLogoLight}
                                alt={appName}
                                className={classes.widgetLogo}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <div>
                              <Typography variant="subtitle2" style={{ color: "white" }}>
                                {values.widgetTitle}
                              </Typography>
                              <Typography variant="caption" style={{ color: "rgba(255,255,255,0.8)" }}>
                                {values.widgetDescription}
                              </Typography>
                            </div>
                          </div>
                          <Button size="small" style={{ color: "white", minWidth: "auto", padding: 4 }}>
                            ×
                          </Button>
                        </div>
                        <div className={classes.widgetBody}>
                          <Typography variant="body2" style={{ textAlign: "center", marginBottom: 16 }}>
                            {values.welcomeMessage}
                          </Typography>
                        </div>
                        <div className={classes.widgetInputArea}>
                          <div className={classes.widgetInput}>
                            {values.messagePlaceholder}
                          </div>
                        </div>
                        <div className={classes.widgetFooter}>
                          <Typography variant="caption">
                            {i18n.t("settings.webchat.preview.poweredBy")} <strong>{appName}</strong>
                          </Typography>
                        </div>
                      </div>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={i18n.t("settings.webchat.form.name")}
                          name="name"
                          value={values.name}
                          onChange={(e) => setFieldValue("name", e.target.value)}
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                          placeholder={i18n.t("settings.webchat.form.namePlaceholder")}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label={i18n.t("settings.webchat.form.allowedOrigins")}
                          name="allowedOrigins"
                          value={values.allowedOrigins}
                          onChange={(e) => setFieldValue("allowedOrigins", e.target.value)}
                          error={touched.allowedOrigins && Boolean(errors.allowedOrigins)}
                          helperText={touched.allowedOrigins ? errors.allowedOrigins : i18n.t("settings.webchat.form.allowedOriginsHelp")}
                          placeholder={i18n.t("settings.webchat.form.allowedOriginsPlaceholder")}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.enabled}
                              onChange={(e) => setFieldValue("enabled", e.target.checked)}
                              name="enabled"
                              color="primary"
                            />
                          }
                          label={i18n.t("settings.webchat.form.enabled")}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label={i18n.t("settings.webchat.form.widgetColor")}
                          name="widgetColor"
                          type="color"
                          value={values.widgetColor}
                          onChange={(e) => setFieldValue("widgetColor", e.target.value)}
                          className={classes.colorPicker}
                          InputProps={{
                            style: { height: 40 }
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>{i18n.t("settings.webchat.form.widgetPosition")}</InputLabel>
                          <Select
                            value={values.widgetPosition}
                            onChange={(e) => setFieldValue("widgetPosition", e.target.value)}
                          >
                            <MenuItem value="right">{i18n.t("settings.webchat.positions.right")}</MenuItem>
                            <MenuItem value="left">{i18n.t("settings.webchat.positions.left")}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={i18n.t("settings.webchat.form.widgetTitle")}
                          name="widgetTitle"
                          value={values.widgetTitle}
                          onChange={(e) => setFieldValue("widgetTitle", e.target.value)}
                          error={touched.widgetTitle && Boolean(errors.widgetTitle)}
                          helperText={touched.widgetTitle && errors.widgetTitle}
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={i18n.t("settings.webchat.form.widgetDescription")}
                          name="widgetDescription"
                          value={values.widgetDescription}
                          onChange={(e) => setFieldValue("widgetDescription", e.target.value)}
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label={i18n.t("settings.webchat.form.welcomeMessage")}
                          name="welcomeMessage"
                          value={values.welcomeMessage}
                          onChange={(e) => setFieldValue("welcomeMessage", e.target.value)}
                          error={touched.welcomeMessage && Boolean(errors.welcomeMessage)}
                          helperText={touched.welcomeMessage && errors.welcomeMessage}
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={i18n.t("settings.webchat.form.messagePlaceholder")}
                          name="messagePlaceholder"
                          value={values.messagePlaceholder}
                          onChange={(e) => setFieldValue("messagePlaceholder", e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={1} className={classes.tabPanel}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>{i18n.t("settings.webchat.form.queueId")}</InputLabel>
                      <Select
                        value={values.queueId || ""}
                        onChange={(e) => setFieldValue("queueId", e.target.value)}
                      >
                        <MenuItem value="">{i18n.t("settings.webchat.messages.noQueue")}</MenuItem>
                        {queues.map((queue) => (
                          <MenuItem key={queue.id} value={queue.id}>
                            {queue.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label={i18n.t("settings.webchat.form.maxConcurrentChats")}
                      name="maxConcurrentChats"
                      value={values.maxConcurrentChats}
                      onChange={(e) => setFieldValue("maxConcurrentChats", parseInt(e.target.value))}
                      error={touched.maxConcurrentChats && Boolean(errors.maxConcurrentChats)}
                      helperText={touched.maxConcurrentChats && errors.maxConcurrentChats}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.autoAssign}
                          onChange={(e) => setFieldValue("autoAssign", e.target.checked)}
                          name="autoAssign"
                          color="primary"
                        />
                      }
                      label={i18n.t("settings.webchat.form.autoAssign")}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.respectBusinessHours}
                          onChange={(e) => setFieldValue("respectBusinessHours", e.target.checked)}
                          name="respectBusinessHours"
                          color="primary"
                        />
                      }
                      label={i18n.t("settings.webchat.form.respectBusinessHours")}
                    />
                  </Grid>
                  
                  {values.respectBusinessHours && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label={i18n.t("settings.webchat.form.offlineMessage")}
                        name="offlineMessage"
                        value={values.offlineMessage}
                        onChange={(e) => setFieldValue("offlineMessage", e.target.value)}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.enableAssistant}
                          onChange={(e) => setFieldValue("enableAssistant", e.target.checked)}
                          name="enableAssistant"
                          color="primary"
                        />
                      }
                      label={i18n.t("settings.webchat.form.enableAssistant")}
                    />
                  </Grid>

                  {values.enableAssistant && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>{i18n.t("settings.webchat.form.assistantId")}</InputLabel>
                        <Select
                          value={values.assistantId || ""}
                          onChange={(e) => setFieldValue("assistantId", e.target.value)}
                          disabled={loadingAssistants}
                        >
                          <MenuItem value="">
                            {loadingAssistants
                              ? i18n.t("settings.webchat.messages.loadingAssistants")
                              : i18n.t("settings.webchat.messages.selectAssistant")
                            }
                          </MenuItem>
                          {assistants
                            .filter(a => a.active)
                            .map((assistant) => (
                              <MenuItem key={assistant.id} value={assistant.id}>
                                {assistant.name} ({assistant.model})
                              </MenuItem>
                            ))
                          }
                        </Select>
                        {assistants.filter(a => a.active).length === 0 && !loadingAssistants && (
                          <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
                            {i18n.t("settings.webchat.help.noAssistants")}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={2} className={classes.tabPanel}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.allowFileUpload}
                          onChange={(e) => setFieldValue("allowFileUpload", e.target.checked)}
                          name="allowFileUpload"
                          color="primary"
                        />
                      }
                      label={i18n.t("settings.webchat.form.allowFileUpload")}
                    />
                  </Grid>
                  
                  {values.allowFileUpload && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label={i18n.t("settings.webchat.form.allowedFileTypes")}
                          name="allowedFileTypes"
                          value={values.allowedFileTypes}
                          onChange={(e) => setFieldValue("allowedFileTypes", e.target.value)}
                          helperText={i18n.t("settings.webchat.help.allowedFileTypes")}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label={i18n.t("settings.webchat.form.maxFileSize")}
                          name="maxFileSize"
                          value={values.maxFileSize}
                          onChange={(e) => setFieldValue("maxFileSize", parseInt(e.target.value))}
                          error={touched.maxFileSize && Boolean(errors.maxFileSize)}
                          helperText={touched.maxFileSize && errors.maxFileSize}
                        />
                      </Grid>
                    </>
                  )}
                  
                  <Grid item xs={12}>
                    <Typography variant="h6" style={{ marginBottom: 16 }}>
                      {i18n.t("settings.webchat.requiredFields")}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.requireName}
                          onChange={(e) => setFieldValue("requireName", e.target.checked)}
                          name="requireName"
                          color="primary"
                        />
                      }
                      label={i18n.t("settings.webchat.form.requireName")}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.requireEmail}
                          onChange={(e) => setFieldValue("requireEmail", e.target.checked)}
                          name="requireEmail"
                          color="primary"
                        />
                      }
                      label={i18n.t("settings.webchat.form.requireEmail")}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.requirePhone}
                          onChange={(e) => setFieldValue("requirePhone", e.target.checked)}
                          name="requirePhone"
                          color="primary"
                        />
                      }
                      label={i18n.t("settings.webchat.form.requirePhone")}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={3} className={classes.tabPanel}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={10}
                      label={i18n.t("settings.webchat.form.customCSS")}
                      name="customCSS"
                      value={values.customCSS || ""}
                      onChange={(e) => setFieldValue("customCSS", e.target.value)}
                      placeholder={i18n.t("settings.webchat.placeholders.customCSS")}
                      helperText={i18n.t("settings.webchat.help.customCSS")}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={4} className={classes.tabPanel}>
                <Typography variant="h6" gutterBottom>
                  {i18n.t("settings.webchat.code.title")}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {i18n.t("settings.webchat.code.description")}
                </Typography>
                {webChat?.token ? (
                  <>
                    <Paper className={classes.codeContainer}>
                      <pre>{generateEmbedCode(webChat.token)}</pre>
                    </Paper>
                    <Button
                      variant="outlined"
                      color="primary"
                      style={{ marginTop: 16 }}
                      onClick={() => {
                        navigator.clipboard.writeText(generateEmbedCode(webChat.token));
                        toastUtils.success(i18n.t("settings.webchat.messages.codeCopied"), i18n.t("settings.webchat.messages.codeCopied"));
                      }}
                    >
                      {i18n.t("settings.webchat.code.copyButton")}
                    </Button>
                  </>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    {i18n.t("settings.webchat.messages.saveToGetCode")}
                  </Typography>
                )}
              </TabPanel>

              <DialogActions>
                <Button onClick={onClose} color="secondary" disabled={isSubmitting}>
                  {i18n.t("settings.webchat.buttons.cancel")}
                </Button>
                <Button type="submit" color="primary" variant="contained" disabled={isSubmitting}>
                  {i18n.t("settings.webchat.buttons.save")}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default WebChatConfigModal;